<template>
  <div>
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5 class="mb-1">Filtros</h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="4" class="mb-md-0 mb-2">
            <b-form-group label="PN" label-for="searchConsultant">
              <all-select
                id="searchConsultant"
                v-model="consultant"
                :options="consultants"
                label="name"
                take-this="id"
                track-by="id"
                multiple
                :close-on-select="false"
                :loading="loading.consultants"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4" class="mb-md-0 mb-2">
            <b-form-group label="Motivo" label-for="searchReason">
              <all-select
                id="searchReason"
                v-model="reason"
                :options="consultantTerminationReasons"
                label="name"
                take-this="id"
                track-by="id"
                multiple
                :close-on-select="false"
                :loading="loading.reasons"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4" class="mb-md-0 mb-2">
            <b-form-group label="Data do desligamento" label-for="terminatedAt">
              <flat-pickr
                id="terminatedAt"
                v-model="terminatedAt"
                class="form-control"
                :config="flatPickrConfig"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" class="mb-md-0 mb-2 text-right">
            <b-button
              variant="outline-secondary"
              @click.prevent="clearSearch"
              class="mr-1"
              :disabled="loading.search"
            >
              Limpar
            </b-button>
            <b-button
              variant="outline-primary"
              @click.prevent="search()"
              :disabled="loading.search"
            >
              <b-spinner v-if="loading.search" small />
              <feather-icon v-else icon="SearchIcon" class="mr-50" />
              <span>{{ loading.search ? "Aguarde..." : "Pesquisar" }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body class="mb-0">
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(currentPage)"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
          <b-col cols="12" md="6" class="text-right">
            <b-button
              variant="outline-primary"
              :disabled="loading.export"
              @click.prevent="exportData()"
            >
              <b-spinner v-if="loading.export" small />
              <feather-icon v-else icon="ArrowDownCircleIcon" class="mr-50" />
              <span>{{ loading.export ? "Aguarde..." : "Exportar planilha" }}</span>
            </b-button>
          </b-col>
        </b-row>
      </div>
      <b-table
        hover
        :items="items"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="Nao foram encontrados desligamentos para esta busca"
        :busy="loading.search"
        @row-clicked="goToConsultantDetails"
      >
        <template #cell(consultant_name)="data">
          <div class="text-wrap">
            <template v-if="data.item.consultant_name">
              <b-button
                :id="`consultant-termination-pop-${data.item.consultant_id}`"
                class="btn-icon"
                variant="flat"
                style="padding: 0"
              >
                <feather-icon size="16" icon="ChevronsUpIcon" class="cursor-pointer center" />
              </b-button>
              <user-detail-popover
                :target="`consultant-termination-pop-${data.item.consultant_id}`"
                type="consultant"
                :id="data.item.consultant_id"
                :showDetailUrl="true"
                :showWhastapp="true"
              />
              <span class="align-text-top text-capitalize">
                {{ data.item.consultant_name }}
              </span>
            </template>
          </div>
        </template>
        <template #cell(reason_name)="data">
          <div class="termination-report-reason">
            <strong>{{ data.item.reason_name }}</strong>
            <small class="text-muted">{{ data.item.reason_description }}</small>
          </div>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">
              Exibindo {{ pageItemsFrom }} a {{ pageItemsTo }} de {{ totalItems }} itens
            </span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              :value="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BFormGroup,
  BPagination,
  BCardHeader,
  BCardBody,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";
import ShortcutButtonsPlugin from "shortcut-buttons-flatpickr";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import todayButton from "@/helpers/todayButton";
import { getRangeDates } from "@/helpers/date_picker";
import * as sharedTypes from "@/modules/shared/store/types";
import * as reportTypes from "../store/types";
import * as consultantTypes from "@/modules/consultant/store/types";
import AllSelect from "@/modules/shared/components/AllSelect";
import UserDetailPopover from "@/modules/shared/components/UserDetailPopover";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BSpinner,
    BFormGroup,
    vSelect,
    flatPickr,
    AllSelect,
    UserDetailPopover,
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      loading: {
        consultants: false,
        reasons: false,
        search: false,
        export: false,
      },
      flatPickrConfig: {
        altFormat: "j M Y",
        altInput: true,
        mode: "range",
        dateFormat: "Y-m-d",
        locale: Portuguese,
        plugins: [ShortcutButtonsPlugin(todayButton)],
      },
      tableColumns: [
        { key: "consultant_name", label: "PN desligado", sortable: false },
        { key: "reason_name", label: "Motivo", sortable: false },
        { key: "additional_details", label: "Detalhamento adicional", sortable: false },
        { key: "formatted_terminated_at", label: "Data do desligamento", sortable: false },
        { key: "terminated_by_name", label: "Usuario responsavel", sortable: false },
      ],
      currentPage: 1,
      itemsPerPage: 1,
      consultant: [],
      reason: [],
      terminatedAt: undefined,
    };
  },
  computed: {
    ...mapGetters({
      consultants: sharedTypes.CONSULTANTS,
      consultantTerminationReasons: consultantTypes.CONSULTANT_TERMINATION_REASONS,
      terminations: reportTypes.CONSULTANT_TERMINATIONS,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    items() {
      return this.terminations?.data || [];
    },
    totalItems() {
      return this.terminations?.total || 0;
    },
    pageItemsFrom() {
      return this.terminations?.from || 0;
    },
    pageItemsTo() {
      return this.terminations?.to || 0;
    },
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage;
    this.search();
    this.loadConsultants();
    this.loadReasons();
  },
  methods: {
    ...mapActions({
      getConsultants: sharedTypes.GET_CONSULTANTS,
      getConsultantTerminationReasons: consultantTypes.GET_CONSULTANT_TERMINATION_REASONS,
      searchTerminations: reportTypes.SEARCH_CONSULTANT_TERMINATIONS,
      exportTerminations: reportTypes.EXPORT_CONSULTANT_TERMINATIONS,
    }),
    loadConsultants() {
      this.loading.consultants = true;
      this.getConsultants()
        .catch(() => this.showError("Ocorreu um erro ao carregar os consultores para selecao."))
        .finally(() => {
          this.loading.consultants = false;
        });
    },
    loadReasons() {
      this.loading.reasons = true;
      this.getConsultantTerminationReasons()
        .catch(() => this.showError("Ocorreu um erro ao carregar os motivos de desligamento."))
        .finally(() => {
          this.loading.reasons = false;
        });
    },
    goToConsultantDetails(row) {
      this.$router.push({
        name: "consultant-details-summary",
        params: { id: row.consultant_id },
      });
    },
    search(currentPage) {
      const terminated = getRangeDates(this.terminatedAt);
      this.loading.search = true;
      this.currentPage = Number.isInteger(currentPage) ? currentPage : 1;
      this.searchTerminations({
        consultant: this.consultant,
        reason: this.reason,
        terminatedAtStart: terminated.start,
        terminatedAtEnd: terminated.end,
        limit: this.itemsPerPage,
        currentPage: this.currentPage,
      })
        .catch(() => this.showError("Ocorreu um erro ao buscar os desligamentos."))
        .finally(() => {
          this.loading.search = false;
        });
    },
    clearSearch() {
      this.consultant = [];
      this.reason = [];
      this.terminatedAt = undefined;
      this.search(1);
    },
    exportData() {
      const terminated = getRangeDates(this.terminatedAt);
      this.loading.export = true;
      this.exportTerminations({
        consultant: this.consultant,
        reason: this.reason,
        terminatedAtStart: terminated.start,
        terminatedAtEnd: terminated.end,
      })
        .catch(() => this.showError("Ocorreu um erro ao exportar os desligamentos."))
        .finally(() => {
          this.loading.export = false;
        });
    },
    showError(text) {
      this.toast({
        component: ToastificationContent,
        props: {
          title: "Oops!",
          text: `${text} Entre em contato com o setor de TI.`,
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.termination-report-reason {
  min-width: 260px;
  white-space: normal;
  line-height: 1.35;

  strong,
  small {
    display: block;
  }

  small {
    margin-top: 0.25rem;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
